/**
 *	The session token, and only the token.
 *
 *	A module-level value rather than context, because the API client reads it on
 *	every request and writes it back whenever a response carries a renewed one —
 *	neither of which happens inside a render. Writes are broadcast so the
 *	session provider stays in step.
 *
 *	Cross-tab synchronisation is deliberately absent: signing out in one tab
 *	leaves another holding a token until its next request answers 401.
 */
import { storage } from '@portal/pkg/storage/local';

const TokenKey = 'session.token';

type TokenListener = (token: string | null) => void;

const listeners: Set<TokenListener> = new Set();

let currentToken: string | null = storage.get<string>(TokenKey, '') || null;

export function readToken(): string | null {
	return currentToken;
}

/**
 *	Replaces the token, but only if `replacing` is still the one in hand.
 *
 *	A renewal belongs to the request it came back on. Without the compare, a
 *	response that was already in flight when the session ended would put a
 *	working token back after `clearToken()` — the screen stays signed out,
 *	because the provider only observes clears, and the next reload signs the
 *	person in again. Logging in passes `null`, which is what there was before.
 */
export function writeToken(token: string, replacing: string | null): void {
	if (token === currentToken || replacing !== currentToken) {
		return;
	}

	currentToken = token;
	storage.set(TokenKey, token);

	for (const listener of listeners) {
		listener(token);
	}
}

/** Called by the API client on 401, and by `logout`. */
export function clearToken(): void {
	if (currentToken === null) {
		return;
	}

	currentToken = null;
	storage.remove(TokenKey);

	for (const listener of listeners) {
		listener(null);
	}
}

export function observeToken(listener: TokenListener): () => void {
	listeners.add(listener);

	return () => {
		listeners.delete(listener);
	};
}
